/**
 * Maya's profile-chat registration script.
 *
 * Each `type: 'ava'` step is a bubble Maya speaks. Each `type: 'input'`
 * step is a free-text answer. Each `type: 'choice'` step is a 3-column
 * grid of selectable buttons. Each `type: 'ocean'` step is one of the
 * 10 Big5 / OCEAN personality questions, rendered with the same chat
 * bubble + a 5-button Likert response row (Not like me at all → Very
 * much like me). The OCEAN steps carry the canonical backend question
 * id under `qid` (`o1`, `o2`, `c1` …) so we can POST the answers to
 * `/api/ocean/submit` after the user account is created.
 *
 * ⚠️ VOICE SYNC: every spoken line here (both `text` and `ava_text`) is
 * whitelisted in backend/app/api/tts.py → MAYA_REGISTRATION_LINES so the
 * public registration TTS endpoint will speak it. If you edit any copy
 * here, update that list too — otherwise the edited line silently falls
 * back to text-only reveal (no voice).
 *
 * Layout — 22 interactive touch-points total, framed by 3 AvA-only
 * chapters that carry the *emotional* arc of the flow:
 *   ── Opening (2 bubbles):    "Hi, I'm Maya + Welcome + set expectations"
 *   ── Q1-Q12 (preferences):   name, age, pronouns, colour, goal, vibe,
 *                              hobby, music, movie, animal, place,
 *                              superpower. These shape the AvA persona,
 *                              meme prompt, and NFT aesthetic.
 *   ── Milestone (2 bubbles):  Halfway celebration + reframes the OCEAN
 *                              block as *feeling statements* with no
 *                              right/wrong answers.
 *   ── Q13-Q22 (OCEAN):        10 Big-5 statements on a 1-5 Likert.
 *                              These personalise the 20-challenge
 *                              library the teen unlocks after signup.
 *   ── Wrap (3 bubbles):       Effort reinforcement + reveals that the
 *                              22 answers will drive 20 unique
 *                              challenges + custom memes + a mintable
 *                              NFT that's unique to them.
 */

// 5-point Likert response options shown for every OCEAN question.
// Kept here so the chat component doesn't need to know the OCEAN
// scoring scale itself.
const OCEAN_RESPONSES = [
  { label: 'Not like me at all',       value: 1 },
  { label: 'Not really like me',       value: 2 },
  { label: 'Somewhere in the middle',  value: 3 },
  { label: 'Quite like me',            value: 4 },
  { label: 'Very much like me',        value: 5 },
];

const SCRIPT = [
  // ── Maya's opening — sets the vibe ─────────────────────────────────────
  { id: 'intro_1', type: 'ava', text: "Hi! I'm Maya, your Practice Manager. Welcome to HiBFF!" },
  { id: 'intro_2', type: 'ava', text: "22 quick questions to make HiBFF 100% yours. No right or wrong answers — ready?" },

  // ── Level 1 — the 5 essentials ─────────────────────────────────────────
  { id: 'q_name',     type: 'input',  field: 'firstName',
    ava_text: "What should I call you? Just a first name or nickname is great.",
    placeholder: 'Your name' },

  { id: 'q_age',      type: 'input',  field: 'age',
    ava_text: "Nice to meet you, {name}! How old are you? (It helps me shape Neo — the friend who'll be right by your side for every challenge.)",
    placeholder: 'Age' },

  { id: 'q_pronouns', type: 'choice', field: 'pronouns',
    ava_text: "Which pronouns do you use? Totally optional — I'll use 'they/them' if you'd rather skip.",
    options: ['she/her', 'he/him', 'they/them', 'something else', 'prefer not to say'] },

  { id: 'q_colour',   type: 'choice', field: 'favoriteColor',
    ava_text: "Pick a colour that feels like YOU today.",
    options: ['Sunset orange', 'Ocean blue', 'Forest green', 'Lavender purple', 'Bubblegum pink', 'Charcoal grey'] },

  { id: 'q_goal',     type: 'choice', field: 'mainGoal',
    ava_text: "What's the #1 thing you'd love help with right now?",
    options: ['Make new friends', 'Talk to people I find intimidating', 'Speak up at school', 'Ask someone out', 'Feel more confident in groups', 'Something else'] },

  // ── Level 2 — preferences that shape AvA persona + meme generation ─────
  { id: 'q_vibe',     type: 'choice', field: 'preferredVibe',
    ava_text: "Now, about Neo — he's your personal challenge buddy. You can personalise him to be exactly the friend you need, and he'll walk you through every one of your challenges step by step. So: what kind of energy do you want Neo to bring?",
    options: ['Chill & calm', 'Hyped & playful', 'Wise & thoughtful', 'Sassy & funny', 'Direct & honest'] },

  { id: 'q_hobby',    type: 'input',  field: 'topHobby', multi: true,
    ava_text: "What's a thing you actually love doing? (Music, sports, gaming, drawing — anything.)",
    placeholder: 'Type one and press Enter — add as many as you like' },

  { id: 'q_music',    type: 'choice', field: 'musicGenre',
    ava_text: "What music do you love listening to right now?",
    options: ['Hip-hop / rap', 'Pop', 'R&B / soul', 'Indie / alt', 'Rock / metal', 'K-pop / J-pop', 'Latin / Afrobeats', 'Country / folk', 'Electronic / EDM'] },

  { id: 'q_movie',    type: 'input',  field: 'favoriteMovie',
    ava_text: "Drop the name of a film or show you'd happily rewatch any time.",
    placeholder: 'A film or show you love' },

  { id: 'q_animal',   type: 'choice', field: 'spiritAnimal',
    ava_text: "If you were any animal for a day, which would it be?",
    options: ['Wolf', 'Fox', 'Cat', 'Eagle', 'Dolphin', 'Lion', 'Owl', 'Horse', 'Dog', 'Something else'] },

  { id: 'q_place',    type: 'choice', field: 'happyPlace',
    ava_text: "Where do you feel most yourself?",
    options: ['My room', 'In nature', 'With my best friends', 'At a concert / live show', 'Playing sport', 'Online / gaming', 'In a quiet cafe'] },

  { id: 'q_strength', type: 'input',  field: 'superpower',
    ava_text: "Last one in this round, {name} — finish this sentence: \"One thing people might not know about me is…\"",
    placeholder: 'Something cool about you' },

  // ── Milestone celebration — fires after the last preference, before OCEAN ─
  // Marks the halfway point (12 done, 10 to go), reframes the second block
  // as *feeling statements* rather than "more questions", and lowers the
  // stakes ("no right or wrong, honest is the only setting").
  { id: 'transition_1', type: 'ava', text: "Amazing work, {name} — that's twelve down, and I already feel like I know you. 🎉 You're halfway there!" },
  { id: 'transition_2', type: 'ava', text: "The next ten are a bit different — they're feeling statements, not questions. There's no right or wrong answer, just pick whichever one sounds most like you at the moment. {name}, try to be as honest as possible — this helps Neo create the best challenges just for you." },

  // ── Level 3 — Big 5 / OCEAN personality (10 statements, 5-pt Likert) ───
  // `qid` matches the canonical backend ids served by GET /api/ocean/questions
  // so the answers can be submitted verbatim after registration completes.
  { id: 'ocean_o1', type: 'ocean', qid: 'o1', trait: 'openness',
    ava_text: "I enjoy exploring new ideas and thinking about things in different ways.", options: OCEAN_RESPONSES },
  { id: 'ocean_o2', type: 'ocean', qid: 'o2', trait: 'openness',
    ava_text: "I like trying things I've never done before, even if I'm not sure how they'll go.", options: OCEAN_RESPONSES },
  { id: 'ocean_c1', type: 'ocean', qid: 'c1', trait: 'conscientiousness',
    ava_text: "When I start something, I usually see it through even when it gets difficult.", options: OCEAN_RESPONSES },
  { id: 'ocean_c2', type: 'ocean', qid: 'c2', trait: 'conscientiousness',
    ava_text: "I like having a plan and knowing what I'm working towards.", options: OCEAN_RESPONSES },
  { id: 'ocean_e1', type: 'ocean', qid: 'e1', trait: 'extraversion',
    ava_text: "I feel more energised after spending time with people than after time alone.", options: OCEAN_RESPONSES },
  { id: 'ocean_e2', type: 'ocean', qid: 'e2', trait: 'extraversion',
    ava_text: "I find it easy to start conversations with people I don't know well.", options: OCEAN_RESPONSES },
  { id: 'ocean_a1', type: 'ocean', qid: 'a1', trait: 'agreeableness',
    ava_text: "I find it natural to think about how other people might be feeling.", options: OCEAN_RESPONSES },
  { id: 'ocean_a2', type: 'ocean', qid: 'a2', trait: 'agreeableness',
    ava_text: "I usually try to find a solution that works for everyone, not just myself.", options: OCEAN_RESPONSES },
  { id: 'ocean_n1', type: 'ocean', qid: 'n1', trait: 'neuroticism',
    ava_text: "I sometimes feel anxious or worried even when things are going okay.", options: OCEAN_RESPONSES },
  { id: 'ocean_n2', type: 'ocean', qid: 'n2', trait: 'neuroticism',
    ava_text: "My mood can change quite quickly depending on what's happening around me.", options: OCEAN_RESPONSES },

  // ── Completion celebration — fires after Q22 (last OCEAN) ──────────────
  // Reinforces the teen/user's effort, then tells them exactly what their
  // answers unlock: 20 personalised challenges, custom memes, and a
  // mintable NFT unique to them. This is the emotional payoff Robert
  // called out — the moment the teen realises the wizard was building
  // *their* HiBFF, not just gathering data.
  { id: 'wrap_1', type: 'ava', text: "Awesome — you did it, {name}! 🎉 You've just given me everything I need to make HiBFF 100% yours." },
  { id: 'wrap_2', type: 'ava', text: "Every answer you give helps shape 20 personalised challenges built just for you. Each one you complete earns a Scout Badge — and from Challenge 10, The Pivot, every badge also mints a unique NFT into your Vault." },
  { id: 'wrap_2b', type: 'ava', text: "After 180 days, your Vault unlocks to reveal your collection — plus, there's an Easter egg hidden around your Vault that drops an instant free bonus straight inside!" },
  { id: 'wrap_2c', type: 'ava', text: "Want an NFT minted for real? Your parent just taps the green Crossmint button on their dashboard — and all the money from any sale goes straight into YOUR wallet." },
  { id: 'wrap_3', type: 'ava', text: "Your journey is about to begin. Tap continue and I'll set up your account." },
];

// ───────────────────────────────────────────────────────────────────────────
//  Abbreviated 4-question script for the `?demo=1` registration shortcut.
//  The 4 questions are deliberately the most emotionally evocative AND the
//  ones that prove there's depth (vs a generic personality quiz):
//    1. spirit animal  — visual + fun  (preference)
//    2. happy place    — emotional, sets the stage  (preference)
//    3. OCEAN o1       — proves there's substance  (Big-5)
//    4. superpower     — always gets a smile  (preference)
//
//  Register.js fills the other 16 answers with sensible neutrals so the
//  resulting user record still has the full `profile` shape and the AvA
//  personality mirroring + meme prompt builder work normally.
// ───────────────────────────────────────────────────────────────────────────
const SCRIPT_DEMO = [
  { id: 'demo_intro', type: 'ava', text: "Hi! I'm Maya. This is a 30-second taste — I'd usually ask 22 questions to really get to know you, but for today let's keep it short. Just 4." },

  { id: 'q_animal', type: 'choice', field: 'spiritAnimal',
    ava_text: "If you were any animal for a day, which would it be?",
    options: ['Wolf', 'Fox', 'Cat', 'Eagle', 'Dolphin', 'Lion', 'Owl', 'Horse', 'Dog', 'Something else'] },

  { id: 'q_place', type: 'choice', field: 'happyPlace',
    ava_text: "Where do you feel most yourself?",
    options: ['My room', 'In nature', 'With my best friends', 'At a concert / live show', 'Playing sport', 'Online / gaming', 'In a quiet cafe'] },

  { id: 'ocean_o1', type: 'ocean', qid: 'o1', trait: 'openness',
    ava_text: "I enjoy exploring new ideas and thinking about things in different ways.", options: OCEAN_RESPONSES },

  { id: 'q_strength', type: 'input', field: 'superpower',
    ava_text: "Last one — finish this sentence: \"One thing people might not know about me is…\"",
    placeholder: 'Something cool about you' },

  { id: 'demo_wrap', type: 'ava', text: "Perfect — that's just a taste. In the full 22-question flow, your answers shape 20 personalised challenges, Scout Badges to collect, and mintable NFTs that are uniquely yours. Tap continue and I'll set up your demo account." },
];

export { SCRIPT, SCRIPT_DEMO, OCEAN_RESPONSES };
export default SCRIPT;
